import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import CloudinaryUpload from '../components/ui/CloudinaryUpload';
import {
  ArrowLeft, Calendar, MapPin, User, Utensils, BedDouble, CreditCard,
  Car, MessageCircle, FileText, HelpCircle, Star, Sparkles, Check,
  Save, AlertCircle, Upload, Banknote, Backpack, Sun, Plus, Trash2,
  Users, Church, Telescope, Send, MessageSquare
} from 'lucide-react';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';

const SECTIONS = [
  { id: 'bienvenida', label: 'Bienvenida', icon: Sparkles },
  { id: 'datos', label: 'Datos personales', icon: User },
  { id: 'alimentacion', label: 'Alimentación', icon: Utensils },
  { id: 'habitacion', label: 'Habitación', icon: BedDouble },
  { id: 'pagos', label: 'Pagos y reserva', icon: CreditCard },
  { id: 'monasterio', label: 'El monasterio', icon: Church },
  { id: 'starlight', label: 'Starlight', icon: Telescope },
  { id: 'boveda', label: 'Bóveda celeste', icon: Star },
  { id: 'coches', label: 'Coches compartidos', icon: Car },
  { id: 'comunicaciones', label: 'Comunicaciones', icon: MessageCircle },
  { id: 'docs', label: 'Documentos y logística', icon: FileText },
  { id: 'faq', label: 'Preguntas frecuentes', icon: HelpCircle },
];

const StatusPill = ({ status }) => {
  const map = {
    waitlist: { label: 'En lista', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    deposit_paid: { label: 'Plaza reservada · paga y señal recibida', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    partially_paid: { label: 'Pago parcial', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    confirmed: { label: 'Confirmada', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    cancelled: { label: 'Cancelada', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  };
  const m = map[status] || { label: status, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  return (
    <span className={`inline-flex items-center text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border ${m.cls}`}
          data-testid={`status-pill-${status}`}>
      {m.label}
    </span>
  );
};

const SectionShell = ({ title, subtitle, children, testId }) => (
  <div className="bg-white rounded-3xl border border-gray-100 p-7 sm:p-9" data-testid={testId}>
    <h2 className="font-heading text-xl sm:text-2xl text-[hsl(var(--text-primary))]">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500 mt-1 mb-5">{subtitle}</p>}
    {!subtitle && <div className="mb-5" />}
    {children}
  </div>
);

const Placeholder = ({ text }) => (
  <div className="text-sm text-gray-400 italic bg-gray-50 rounded-xl p-5 border border-dashed border-gray-200">
    {text}
  </div>
);

const RetreatPrivate = () => {
  const { slug: routeSlug } = useParams();
  const slug = routeSlug || 'reinicia-2026';
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('bienvenida');
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!user) { navigate('/login?redirect=/mi-retiro/' + slug); return; }
    api.get(`/retreats/${slug}/me`)
      .then(r => setData(r.data))
      .catch(() => toast.error('No pudimos cargar tu espacio'))
      .finally(() => setLoading(false));
  }, [slug, user, navigate]);

  useEffect(() => {
    // Refresh the countdown every minute so it stays accurate without manual reload
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return <div className="min-h-screen pt-32 flex items-center justify-center bg-[#FDFBF5]">
      <div className="w-8 h-8 border-2 border-[#D4A84B] border-t-transparent rounded-full animate-spin" />
    </div>;
  }
  if (!data?.enrollment) {
    return (
      <div className="min-h-screen pt-28 bg-[#FDFBF5] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="text-gray-600 mb-6">Aún no tienes plaza reservada en este retiro.</p>
          <Link to={`/retiros/${slug}`} className="inline-flex items-center gap-2 bg-[#2d3748] text-white px-6 py-3 rounded-full text-sm">
            Ir al retiro
          </Link>
        </div>
      </div>
    );
  }

  const { retreat, enrollment } = data;
  const dStart = new Date(retreat.date_start);
  const daysToGo = Math.max(0, Math.ceil((dStart - now) / (1000 * 60 * 60 * 24)));

  return (
    <div className="min-h-screen bg-[#FDFBF5] pt-24 pb-20" data-testid="retreat-private-panel">
      <div className="max-w-6xl mx-auto px-6">
        <Link to="/mi-camino" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Volver a Mi Camino
        </Link>

        {/* Header — banner 50/50 dorado + foto del monasterio */}
        <div
          className="relative rounded-3xl border border-[#D4A84B]/30 overflow-hidden mb-8 shadow-md"
          data-testid="retreat-header"
        >
          <div className="grid sm:grid-cols-2 min-h-[280px] sm:min-h-[320px]">
            {/* Left — panel dorado con degradado */}
            <div
              className="relative flex flex-col justify-center px-7 sm:px-9 py-8 sm:py-10 text-[#3a2e10]"
              style={{
                background: 'linear-gradient(135deg, #E8C56B 0%, #D4A84B 55%, #b89220 100%)',
              }}
            >
              {/* Texture overlay sutil */}
              <div
                className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-overlay"
                style={{
                  backgroundImage: "radial-gradient(circle at 20% 20%, white 0.5px, transparent 1px), radial-gradient(circle at 80% 60%, white 0.5px, transparent 1px)",
                  backgroundSize: '24px 24px, 32px 32px',
                }}
              />
              <div className="relative">
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/85 mb-3">
                  Tu espacio · Retiro presencial
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] leading-[0.95] text-white">
                  REINICIA
                </h1>
                <p
                  className="text-2xl sm:text-3xl lg:text-[2rem] text-[#3a2e10]/85 mt-2 leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
                >
                  Descanso Interno
                </p>
                <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-sm text-white">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" /> {retreat.date_start} · {retreat.date_end}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" /> {retreat.location_city}
                  </span>
                </div>
                <div className="mt-5 inline-flex items-center gap-3 bg-[#1a140a]/10 backdrop-blur-sm rounded-full px-3 py-1.5">
                  <StatusPill status={enrollment.status} />
                  {daysToGo > 0 && (
                    <span className="text-xs text-white">
                      faltan <strong>{daysToGo} días</strong>
                    </span>
                  )}
                </div>
              </div>
              {/* Right edge fade so the photo blends in */}
              <div
                className="hidden sm:block absolute inset-y-0 -right-px w-24 pointer-events-none"
                style={{ background: 'linear-gradient(to right, transparent 0%, rgba(232,197,107,0) 30%, rgba(184,146,32,0.0) 100%)' }}
              />
            </div>

            {/* Right — foto del monasterio */}
            <div className="relative min-h-[200px] sm:min-h-0">
              <img
                src="https://res.cloudinary.com/dpppkdhdk/image/upload/v1779985377/OI000470_iajyaj.jpg"
                alt="Monasterio Santa María de El Olivar"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Suave degradado de la foto hacia el dorado en el borde izquierdo */}
              <div
                className="absolute inset-y-0 left-0 w-32 pointer-events-none"
                style={{ background: 'linear-gradient(to right, rgba(212,168,75,0.85) 0%, rgba(212,168,75,0.35) 50%, transparent 100%)' }}
              />
            </div>
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto" data-testid="section-tabs">
          {SECTIONS.map(s => {
            const Icon = s.icon;
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-[#2d3748] text-white shadow-sm'
                    : 'bg-white text-gray-600 hover:bg-[#FDFBF5] border border-gray-200'
                }`}
                data-testid={`tab-${s.id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Sections */}
        {active === 'bienvenida' && <BienvenidaSection retreat={retreat} enrollment={enrollment} daysToGo={daysToGo} />}
        {active === 'datos' && <DatosSection slug={slug} enrollment={enrollment} onUpdate={setData} />}
        {active === 'alimentacion' && <DietSection slug={slug} enrollment={enrollment} onUpdate={setData} />}
        {active === 'habitacion' && <RoomSection slug={slug} enrollment={enrollment} onUpdate={setData} />}
        {active === 'pagos' && <PaymentsSection slug={slug} retreat={retreat} enrollment={enrollment} onUpdate={setData} />}
        {active === 'monasterio' && <MonasterioSection />}
        {active === 'starlight' && <StarlightInfoSection />}
        {active === 'boveda' && <StarlightSection slug={slug} enrollment={enrollment} onUpdate={setData} />}
        {active === 'coches' && <CarpoolSection slug={slug} />}
        {active === 'comunicaciones' && <CommsSection slug={slug} />}
        {active === 'docs' && <DocsSection retreat={retreat} />}
        {active === 'faq' && <FaqSection retreat={retreat} />}

        {/* Floating WhatsApp helper — always visible */}
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-40 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4 py-3 shadow-xl hover:shadow-2xl transition-all flex items-center gap-2 group"
          title="Escríbele a Hermi por WhatsApp"
          data-testid="floating-whatsapp"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-xs font-medium hidden sm:inline group-hover:inline">WhatsApp con Hermi</span>
        </a>
      </div>
    </div>
  );
};

const BienvenidaSection = ({ retreat, enrollment, daysToGo }) => (
  <SectionShell
    title="Bienvenida"
    subtitle="Empieza por aquí cada vez que entres."
    testId="section-bienvenida"
  >
    {/* Cita destacada */}
    <div className="relative bg-gradient-to-br from-[#FBF6E8] via-[#FDFBF5] to-white rounded-3xl border border-[#D4A84B]/30 p-6 sm:p-7 mb-6 overflow-hidden">
      <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-[#D4A84B]/10 blur-2xl" />
      <Sparkles className="w-5 h-5 text-[#D4A84B] mb-3" />
      <p className="text-base sm:text-lg text-[hsl(var(--text-primary))] leading-relaxed italic relative" style={{ fontFamily: 'Georgia, serif' }}>
        "{retreat.quote_highlight || 'Bienvenida. Aquí ya estás un poco más cerca.'}"
      </p>
    </div>

    {/* Carta de bienvenida */}
    <div className="bg-white border border-gray-100 rounded-3xl p-6 sm:p-8 mb-6" data-testid="welcome-letter">
      <div className="flex items-center gap-2 mb-4">
        <span className="h-px w-8 bg-[#D4A84B]" />
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8a7428]">Carta de bienvenida</p>
        <span className="h-px flex-1 bg-[#D4A84B]/30" />
      </div>
      <div className="space-y-4 text-sm sm:text-[15px] text-gray-700 leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
        {/* Foto de Hermi flotante, integrada con el texto */}
        <img
          src="https://res.cloudinary.com/dpppkdhdk/image/upload/v1779988799/ChatGPT_Image_7_may_2026_18_45_40_6_xvlyr6.png"
          alt="Hermi"
          loading="lazy"
          className="float-right ml-5 mb-3 w-32 h-32 sm:w-44 sm:h-44 rounded-full object-cover ring-2 ring-[#D4A84B]/40 shadow-md shape-outside-circle"
          style={{ shapeOutside: 'circle(50%)' }}
          data-testid="hermi-portrait"
        />
        <p>
          Querid@{enrollment.personal_data?.full_name ? <> <span className="text-[#8a7428] not-italic font-medium">{enrollment.personal_data.full_name.split(' ')[0]}</span>,</> : ','}
        </p>
        <p>
          Gracias por decir <em>sí</em>. Por hacer un hueco real en tu calendario y en tu vida
          para volver a ti. Lo más importante ya lo has hecho.
        </p>
        <p>
          Este espacio es tu casa antes de la casa. Aquí encontrarás todo lo que necesitas saber
          —los datos para el monasterio, tu habitación, los pagos, lo que conviene traer—
          y también un par de rincones para hablarle al cielo de Teruel antes de que llegue julio.
        </p>
        <p>
          Te invito a ir despacio:
        </p>
        <ul className="space-y-2 pl-1 not-italic" style={{ fontFamily: 'inherit' }}>
          <li className="flex gap-3 text-gray-600 text-[14px]">
            <span className="text-[#D4A84B] flex-shrink-0">·</span>
            <span>Completa <strong>Datos personales</strong> y <strong>Alimentación</strong>. Es lo que el monasterio necesita.</span>
          </li>
          <li className="flex gap-3 text-gray-600 text-[14px]">
            <span className="text-[#D4A84B] flex-shrink-0">·</span>
            <span>Elige tu <strong>habitación</strong> y, si quieres, propón compañera.</span>
          </li>
          <li className="flex gap-3 text-gray-600 text-[14px]">
            <span className="text-[#D4A84B] flex-shrink-0">·</span>
            <span>Confirma tu plaza con la <strong>paga y señal</strong> (100€). El resto, antes del 4 de julio.</span>
          </li>
          <li className="flex gap-3 text-gray-600 text-[14px]">
            <span className="text-[#D4A84B] flex-shrink-0">·</span>
            <span>Pásate por <strong>Bóveda celeste</strong> a dejar tu intención bajo las estrellas.</span>
          </li>
          <li className="flex gap-3 text-gray-600 text-[14px]">
            <span className="text-[#D4A84B] flex-shrink-0">·</span>
            <span>Revisa <strong>Qué llevar</strong> con calma cuando se acerque la fecha.</span>
          </li>
        </ul>
        <p>
          No tienes que prepararte para "ser mejor". Sólo necesitas <strong>llegar</strong>.
          Del resto, nos ocupamos.
        </p>
        <div className="clear-both flex items-center justify-end gap-3 mt-6" data-testid="hermi-signature">
          <p className="text-[#8a7428] text-xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}>
            — Hermi
          </p>
        </div>
      </div>
    </div>

    {/* Intención personal */}
    {enrollment.intention_text && (
      <div className="bg-gradient-to-br from-white to-[#FDFBF5] border border-[#D4A84B]/20 rounded-2xl p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-3.5 h-3.5 text-[#D4A84B] fill-current" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a7428]">Tu intención al inscribirte</p>
        </div>
        <p className="text-sm text-gray-700 italic leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
          "{enrollment.intention_text}"
        </p>
      </div>
    )}

    {/* Countdown */}
    {daysToGo > 0 && daysToGo <= 30 && (
      <div className="mt-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900 flex items-start gap-3">
        <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
        <div>
          <p className="font-medium mb-1">A {daysToGo} días del encuentro</p>
          <p className="text-amber-800/80">Es buen momento para revisar tus datos personales, cerrar la habitación y echar un ojo a qué llevar.</p>
        </div>
      </div>
    )}
  </SectionShell>
);

const SaveButton = ({ onClick, loading }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="inline-flex items-center gap-2 bg-[#2d3748] hover:bg-black text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors disabled:opacity-60"
    data-testid="save-btn"
  >
    <Save className="w-4 h-4" />
    {loading ? 'Guardando…' : 'Guardar'}
  </button>
);

const DatosSection = ({ slug, enrollment, onUpdate }) => {
  const [form, setForm] = useState({
    full_name: enrollment.personal_data?.full_name || enrollment.user_name || '',
    dni: enrollment.personal_data?.dni || '',
    date_of_birth: enrollment.personal_data?.date_of_birth || '',
    nationality: enrollment.personal_data?.nationality || 'España',
    phone: enrollment.personal_data?.phone || '',
    emergency_contact_name: enrollment.personal_data?.emergency_contact_name || '',
    emergency_contact_phone: enrollment.personal_data?.emergency_contact_phone || '',
    address_line: enrollment.personal_data?.address_line || '',
    city: enrollment.personal_data?.city || '',
    postal_code: enrollment.personal_data?.postal_code || '',
    image_consent: enrollment.personal_data?.image_consent ?? true,
    notes: enrollment.personal_data?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/retreats/${slug}/me/personal-data`, form);
      toast.success('Datos guardados');
      const r = await api.get(`/retreats/${slug}/me`);
      onUpdate(r.data);
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, type = 'text') => (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">{label}</span>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
        data-testid={`field-${key}`}
      />
    </label>
  );

  return (
    <SectionShell
      title="Datos personales"
      subtitle="Estos datos son los que el monasterio necesita para tu estancia."
      testId="section-datos"
    >
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6 text-xs text-amber-800 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        El monasterio está obligado por ley a registrar tus datos identificativos. Solo nosotros los vemos.
      </div>
      {enrollment.user_email && (
        <div className="mb-4">
          <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Correo electrónico (tu cuenta)</span>
          <div className="w-full px-3 py-2.5 border border-gray-100 rounded-xl text-sm bg-gray-50 text-gray-600 inline-flex items-center gap-2" data-testid="field-email">
            <span className="truncate">{enrollment.user_email}</span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Para cambiarlo, escríbenos a hola@herminiovazquez.com</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        {field('full_name', 'Nombre completo')}
        {field('dni', 'DNI / Pasaporte')}
        {field('date_of_birth', 'Fecha de nacimiento', 'date')}
        {field('nationality', 'Nacionalidad')}
        {field('phone', 'Teléfono', 'tel')}
        {field('city', 'Ciudad de residencia')}
        {field('address_line', 'Dirección')}
        {field('postal_code', 'Código postal')}
        {field('emergency_contact_name', 'Contacto de emergencia (nombre)')}
        {field('emergency_contact_phone', 'Contacto de emergencia (teléfono)', 'tel')}
      </div>
      <label className="flex items-start gap-2 text-sm text-gray-600 mb-5">
        <input
          type="checkbox"
          checked={form.image_consent}
          onChange={(e) => setForm({ ...form, image_consent: e.target.checked })}
          className="mt-1"
          data-testid="field-image-consent"
        />
        <span>Doy mi consentimiento para que se puedan tomar fotos grupales del retiro (uso interno y, ocasionalmente, redes sociales de la escuela).</span>
      </label>
      <SaveButton onClick={handleSave} loading={saving} />
    </SectionShell>
  );
};

const DietSection = ({ slug, enrollment, onUpdate }) => {
  const [form, setForm] = useState({
    diet_type: enrollment.diet?.diet_type || 'de la tierra',
    allergies: enrollment.diet?.allergies || '',
    intolerances: enrollment.diet?.intolerances || '',
    medication: enrollment.diet?.medication || '',
    extra_notes: enrollment.diet?.extra_notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/retreats/${slug}/me/diet`, form);
      toast.success('Alimentación actualizada');
      const r = await api.get(`/retreats/${slug}/me`);
      onUpdate(r.data);
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <SectionShell title="Alimentación e intolerancias" subtitle="El monasterio adapta los menús a cada persona." testId="section-alimentacion">
      {/* Huerto propio */}
      <div className="mb-6 grid sm:grid-cols-[1fr_220px] gap-4 items-center bg-gradient-to-br from-emerald-50/40 via-[#FDFBF5] to-white border border-emerald-200/40 rounded-2xl p-4 sm:p-5" data-testid="huerto-block">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-emerald-700" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-800">Huerto propio</p>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">
            El monasterio cultiva <strong className="text-emerald-800">su propio huerto</strong> para abastecer la cocina
            con alimentos frescos de temporada. Lo que comerás durante el retiro está,
            en buena parte, hecho a unos metros de la mesa.
          </p>
        </div>
        <img
          src="https://res.cloudinary.com/dpppkdhdk/image/upload/v1779984246/OI000018_n63t5z.jpg"
          alt="Huerto del monasterio"
          loading="lazy"
          className="w-full h-32 sm:h-36 object-cover rounded-xl shadow-sm"
          data-testid="huerto-photo"
        />
      </div>

      <label className="block mb-4">
        <span className="block text-xs uppercase tracking-wider text-gray-500 mb-2">Tipo de dieta</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {['de la tierra','vegetariana','vegana','flexitariana'].map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => setForm({ ...form, diet_type: opt })}
              className={`px-3 py-2 rounded-xl text-xs border transition-colors capitalize ${
                form.diet_type === opt
                  ? 'bg-[#D4A84B]/20 border-[#D4A84B] text-[#5e4f1a] font-medium'
                  : 'bg-white border-gray-200 text-gray-600'
              }`}
              data-testid={`diet-${opt}`}
            >{opt}</button>
          ))}
        </div>
      </label>
      {['allergies','intolerances','medication','extra_notes'].map(key => (
        <label key={key} className="block mb-3">
          <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">
            {{allergies:'Alergias',intolerances:'Intolerancias',medication:'Medicación actual',extra_notes:'Otras notas'}[key]}
          </span>
          <textarea
            rows={2}
            value={form[key]}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none resize-none"
            data-testid={`diet-field-${key}`}
          />
        </label>
      ))}
      <SaveButton onClick={handleSave} loading={saving} />
    </SectionShell>
  );
};

const ROOM_PHOTOS = [
  { url: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779980322/habitacio%CC%81n_individual_bjtnye.jpg', label: 'Habitación individual' },
  { url: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779980322/habitacion_doble_g0vpit.jpg', label: 'Habitación doble compartida' },
  { url: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779980321/ban%CC%83o_habitacion_doble_w1lpcu.jpg', label: 'Baño habitación doble' },
  { url: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779980322/vistas_habitacion_e3iatp.jpg', label: 'Vistas desde la habitación' },
  { url: 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779980321/vistas_hab_individual_dnq4kf.jpg', label: 'Vistas individual' },
];

const RoomSection = ({ slug, enrollment, onUpdate }) => {
  const [pref, setPref] = useState(enrollment.room_preference === 'either' ? 'share' : (enrollment.room_preference || 'share'));
  const [saving, setSaving] = useState(false);
  const [matchEmail, setMatchEmail] = useState(enrollment.room_match_request || '');
  const [matchNote, setMatchNote] = useState(enrollment.room_match_note || '');
  const [savingMatch, setSavingMatch] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/retreats/${slug}/me/room-preference`, { room_preference: pref });
      toast.success('Preferencia guardada');
      const r = await api.get(`/retreats/${slug}/me`);
      onUpdate(r.data);
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const saveMatch = async () => {
    setSavingMatch(true);
    try {
      await api.put(`/retreats/${slug}/me/room-match-request`, {
        room_match_request: matchEmail.trim(),
        room_match_note: matchNote.trim(),
      });
      toast.success('Petición guardada. Hermi lo revisará.');
      const r = await api.get(`/retreats/${slug}/me`);
      onUpdate(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSavingMatch(false); }
  };

  return (
    <SectionShell title="Habitación" subtitle="Tu preferencia de alojamiento durante el retiro." testId="section-habitacion">
      {/* Galería de fotos */}
      <div className="mb-7" data-testid="room-photos">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-px w-6 bg-[#D4A84B]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a7428]">Así son las habitaciones</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {ROOM_PHOTOS.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(p)}
              className="group relative overflow-hidden rounded-xl bg-gray-100 aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-[#D4A84B]"
              data-testid={`room-photo-${i}`}
            >
              <img
                src={p.url}
                alt={p.label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent p-2">
                <p className="text-[10px] text-white/95 font-medium leading-tight">{p.label}</p>
              </div>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-gray-400 mt-2 italic">Pulsa para ampliar.</p>
      </div>

      {/* Preferencia */}
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        {[
          { v: 'share',  l: 'Habitación doble compartida', desc: 'La opción principal · 452,50€ por persona · oportunidad de tejer vínculo' },
          { v: 'single', l: 'Habitación individual', desc: 'Para quien necesita silencio absoluto · 512,50€ · sujeta a aprobación (solo 3 plazas)' },
        ].map(opt => (
          <button
            key={opt.v}
            type="button"
            onClick={() => setPref(opt.v)}
            className={`text-left p-4 rounded-2xl border transition-colors ${
              pref === opt.v
                ? 'bg-[#D4A84B]/10 border-[#D4A84B] ring-1 ring-[#D4A84B]/40'
                : 'bg-white border-gray-200 hover:border-[#D4A84B]/40'
            }`}
            data-testid={`room-${opt.v}`}
          >
            <p className="font-medium text-[hsl(var(--text-primary))] mb-1">{opt.l}</p>
            <p className="text-xs text-gray-500">{opt.desc}</p>
          </button>
        ))}
      </div>

      {/* Single room status messages */}
      {enrollment.room_preference === 'single' && !enrollment.single_room_approved && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-900 flex items-start gap-2" data-testid="single-pending-notice">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Has solicitado habitación individual</p>
            <p className="mt-1 text-amber-800/90">
              Estamos revisando disponibilidad (solo hay 3 plazas individuales). Te confirmaremos en cuanto la asignemos.
              Mientras tanto, en pagos verás el precio de habitación doble (452,50€).
            </p>
          </div>
        </div>
      )}
      {enrollment.room_preference === 'single' && enrollment.single_room_approved && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 text-xs text-emerald-900 flex items-start gap-2" data-testid="single-approved-notice">
          <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Habitación individual confirmada</p>
            <p className="mt-1 text-emerald-800/90">Tu precio total es 512,50€.</p>
          </div>
        </div>
      )}

      <SaveButton onClick={save} loading={saving} />

      {/* Nota de la hospedería monástica */}
      <div className="mt-6 bg-amber-50/60 border border-amber-200/70 rounded-xl p-4 text-xs text-amber-900 leading-relaxed" data-testid="hospitality-note">
        <p className="font-medium text-amber-800 mb-1">Una nota sobre el estilo de acogida</p>
        <p>
          Por el estilo de acogida monástica, <strong>no se hace servicio diario de limpieza</strong> de habitaciones.
          Podrás disponer de material para arreglarte tu habitación y, cuando lo necesites, solicitar toallas limpias en recepción.
        </p>
      </div>

      {/* Matching */}
      {pref === 'share' && (
        <div className="mt-7 bg-gradient-to-br from-[#FDFBF5] to-white border border-[#D4A84B]/20 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <BedDouble className="w-4 h-4 text-[#8a7428]" />
            <p className="text-sm font-medium text-[#5e4f1a]">¿Quieres compartir con alguien concreta?</p>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed mb-4">
            Escribe el email de la persona con la que te gustaría compartir habitación.
            Si ella también te indica a ti, lo dejamos cerrado. Si no, asignamos por compatibilidad.
          </p>
          <label className="block mb-3">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Email de tu compañera deseada</span>
            <input
              type="email"
              value={matchEmail}
              onChange={(e) => setMatchEmail(e.target.value)}
              placeholder="ej. carmen@ejemplo.com"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
              data-testid="room-match-email"
            />
          </label>
          <label className="block mb-4">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Nota (opcional)</span>
            <input
              type="text"
              value={matchNote}
              onChange={(e) => setMatchNote(e.target.value)}
              placeholder="ej. somos amigas, ya lo hablamos"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
              data-testid="room-match-note"
            />
          </label>
          {enrollment.room_match && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-3 text-xs text-emerald-800 flex items-center gap-2">
              <Check className="w-3.5 h-3.5" /> Asignada con: <strong>{enrollment.room_match}</strong>
            </div>
          )}
          <button
            onClick={saveMatch}
            disabled={savingMatch}
            className="inline-flex items-center gap-2 bg-[#2d3748] hover:bg-black text-white px-4 py-2 rounded-full text-xs font-medium disabled:opacity-60"
            data-testid="save-room-match"
          >
            <Save className="w-3.5 h-3.5" />
            {savingMatch ? 'Guardando…' : 'Guardar petición'}
          </button>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          data-testid="room-lightbox"
        >
          <img src={lightbox.url} alt={lightbox.label} className="max-h-[92vh] max-w-[92vw] rounded-xl shadow-2xl" />
        </div>
      )}
    </SectionShell>
  );
};

const PaymentsSection = ({ slug, retreat, enrollment, onUpdate }) => {
  // Honor admin price_override; otherwise compute from room preference + approval status.
  // Only use single price if user picked 'single' AND admin has approved it.
  const [specialPricing, setSpecialPricing] = useState(null);
  useEffect(() => {
    api.get(`/retreats/${slug}/special-pricing/me`)
      .then(r => { if (r.data?.eligible) setSpecialPricing(r.data); })
      .catch(() => {});
  }, [slug]);

  let finalPrice;
  let usingSinglePrice = false;
  let usingSpecialPricing = false;
  let regularDoublePrice = null;
  if (enrollment.price_override != null && enrollment.price_override !== '') {
    finalPrice = Number(enrollment.price_override) || 0;
  } else {
    const useSingle = enrollment.room_preference === 'single' && enrollment.single_room_approved === true;
    usingSinglePrice = useSingle;
    const earlyBirdActive = retreat.pricing.early_bird_until && (new Date() <= new Date(retreat.pricing.early_bird_until));
    if (!useSingle && enrollment.room_preference === 'share' && specialPricing) {
      // Tarifa alumnado actual — overrides the regular double price (no late surcharge).
      regularDoublePrice = Number(specialPricing.regular_price_double);
      finalPrice = Number(specialPricing.price_double);
      usingSpecialPricing = true;
    } else {
      const basePrice = useSingle ? retreat.pricing.single.amount : retreat.pricing.double.amount;
      finalPrice = earlyBirdActive ? basePrice : (basePrice + (retreat.pricing.late_surcharge || 0));
    }
  }
  const earlyBirdActive = retreat.pricing.early_bird_until && (new Date() <= new Date(retreat.pricing.early_bird_until));
  const singleRequestedPending = enrollment.room_preference === 'single' && !enrollment.single_room_approved;
  const deposit = retreat.pricing.deposit_amount || 100;
  const paid = (enrollment.payments || []).reduce((s, p) => s + (p.status === 'confirmed' ? p.amount : 0), 0);
  const pendingValidation = (enrollment.payments || []).filter(p => p.status === 'pending_validation');
  const remaining = Math.max(0, finalPrice - paid);

  // Default suggested amount: paga y señal first, then remaining
  const suggested = paid < deposit ? Math.min(deposit, remaining) : remaining;
  const [amount, setAmount] = useState(suggested);
  const [tab, setTab] = useState('paypal'); // paypal | transfer
  const [receiptUrl, setReceiptUrl] = useState('');
  const [method, setMethod] = useState('transfer'); // transfer | bizum
  const [note, setNote] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { setAmount(suggested); }, [suggested]);

  const refresh = async () => {
    const r = await api.get(`/retreats/${slug}/me`);
    onUpdate(r.data);
  };

  // PayPal flow
  const paypalActions = {
    createOrder: async () => {
      try {
        const { data } = await api.post(`/retreats/${slug}/me/payments/paypal/create`, { amount });
        if (!data.order_id) throw new Error('No order_id');
        return data.order_id;
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No se pudo iniciar el pago');
        throw e;
      }
    },
    onApprove: async (pp) => {
      setProcessing(true);
      try {
        await api.post(`/retreats/${slug}/me/payments/paypal/capture`, { order_id: pp.orderID, amount });
        toast.success('¡Pago confirmado! Gracias.');
        await refresh();
      } catch (e) {
        toast.error('Error al capturar el pago');
      } finally {
        setProcessing(false);
      }
    },
    onError: () => toast.error('Error con PayPal'),
  };

  const submitTransfer = async () => {
    if (!receiptUrl) {
      toast.error('Adjunta el comprobante');
      return;
    }
    if (amount <= 0) { toast.error('Importe inválido'); return; }
    setProcessing(true);
    try {
      await api.post(`/retreats/${slug}/me/payments/transfer`, {
        amount, method, receipt_url: receiptUrl, note,
      });
      toast.success('Comprobante enviado. Hermi lo validará pronto.');
      setReceiptUrl(''); setNote('');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar');
    } finally {
      setProcessing(false);
    }
  };

  const formatMethod = (m) => ({ paypal: 'PayPal', transfer: 'Transferencia', bizum: 'Bizum' }[m] || m);
  const formatStatus = (s) => ({
    confirmed: { label: 'Confirmado', cls: 'bg-emerald-50 text-emerald-700' },
    pending_validation: { label: 'En validación', cls: 'bg-amber-50 text-amber-700' },
    rejected: { label: 'Rechazado', cls: 'bg-red-50 text-red-700' },
  }[s] || { label: s, cls: 'bg-gray-50 text-gray-600' });

  return (
    <SectionShell title="Pagos y reserva" subtitle="Tu plaza queda confirmada con la paga y señal." testId="section-pagos">
      {/* Aviso de tipo de habitación */}
      {singleRequestedPending && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-900 flex items-start gap-3" data-testid="payments-single-pending">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium mb-1">Has solicitado habitación individual · pendiente de aprobación</p>
            <p className="text-amber-800/90 leading-relaxed">
              De momento puedes pagar el precio de <strong>habitación doble (452,50€)</strong>. Cuando confirmemos tu individual, te notificaremos para que abones la diferencia (60€).
            </p>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-500 italic mb-5">
        💡 Si quieres cambiar de tipo de habitación, hazlo primero en la pestaña <strong>Habitación</strong>. El precio aquí se ajusta automáticamente.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 mb-7">
        <div className={`bg-[#FDFBF5] border border-[#D4A84B]/30 rounded-2xl p-5 ${usingSpecialPricing ? 'sm:col-span-1 relative overflow-hidden' : ''}`}>
          <p className="text-xs uppercase tracking-wider text-[#8a7428] mb-1">Precio total</p>
          {usingSpecialPricing && regularDoublePrice && (
            <p className="text-sm text-gray-400 line-through leading-none mb-1" data-testid="total-price-original">
              {regularDoublePrice.toFixed(2)} €
            </p>
          )}
          <p className="font-heading text-2xl text-[#8a7428]" data-testid="total-price">{finalPrice.toFixed(2)} €</p>
          <p className="text-xs text-gray-500 mt-1">
            {enrollment.price_override != null && enrollment.price_override !== ''
              ? <em className="text-[#8a7428]">Precio personalizado</em>
              : usingSpecialPricing
                ? <span className="inline-flex items-center gap-1 text-[#8a7428]"><Sparkles className="w-3 h-3" /> {specialPricing?.title || 'Precio alumnado actual'}</span>
                : usingSinglePrice
                  ? 'Habitación individual'
                  : 'Habitación doble (compartida)'}
            {enrollment.price_override == null && !usingSpecialPricing && !earlyBirdActive && <span className="block text-amber-600 mt-1">Incluye recargo +{retreat.pricing.late_surcharge}€</span>}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Aportado</p>
          <p className="font-heading text-2xl text-emerald-600">{paid.toFixed(2)} €</p>
          {pendingValidation.length > 0 && (
            <p className="text-xs text-amber-600 mt-1">+{pendingValidation.reduce((s,p)=>s+p.amount,0).toFixed(2)}€ en validación</p>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Pendiente</p>
          <p className="font-heading text-2xl text-gray-700">{remaining.toFixed(2)} €</p>
        </div>
      </div>

      {usingSpecialPricing && (
        <div
          className="bg-gradient-to-r from-[#FDFBF5] via-white to-[#FDFBF5] border border-[#D4A84B]/40 rounded-2xl p-5 mb-6 flex items-start gap-3"
          data-testid="special-pricing-message"
        >
          <Sparkles className="w-4 h-4 text-[#D4A84B] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#5e4f1a] leading-relaxed">
            <strong>{specialPricing?.title || 'Precio alumnado actual'}.</strong>{' '}
            Como gesto de gratitud por estar en la formación, te corresponde un precio especial.
          </p>
        </div>
      )}

      {remaining > 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-6">
          <p className="text-sm text-gray-600 leading-relaxed mb-5">
            {paid < deposit
              ? <>Paga ahora la <strong>paga y señal de {deposit}€</strong> para confirmar tu plaza. El resto puedes abonarlo de una vez antes del <strong>4 de julio</strong>.</>
              : <>Aún quedan <strong>{remaining.toFixed(2)}€</strong> por abonar. Puedes pagarlos de una vez o en varios momentos.</>}
          </p>

          {/* Amount input */}
          <label className="block mb-4">
            <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Importe a pagar (€)</span>
            <input
              type="number"
              min="1"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              max={remaining}
              className="w-full sm:w-48 px-3 py-2.5 border border-gray-200 rounded-xl text-base font-heading focus:border-[#D4A84B] focus:outline-none"
              data-testid="payment-amount"
            />
            <span className="ml-3 text-xs text-gray-500">máx. {remaining.toFixed(2)}€</span>
          </label>

          {/* Method tabs */}
          <div className="flex gap-2 mb-5 border-b border-gray-100">
            {[
              { v: 'paypal', l: 'PayPal · tarjeta' },
              { v: 'transfer', l: 'Transferencia o Bizum' },
            ].map(t => (
              <button
                key={t.v}
                type="button"
                onClick={() => setTab(t.v)}
                className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === t.v ? 'border-[#D4A84B] text-[#8a7428] font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid={`pay-tab-${t.v}`}
              >{t.l}</button>
            ))}
          </div>

          {tab === 'paypal' && (
            <div className={processing ? 'pointer-events-none opacity-50' : ''}>
              {PAYPAL_CLIENT_ID && amount > 0 ? (
                <PayPalScriptProvider
                  key={`pp-${amount}`}
                  options={{ clientId: PAYPAL_CLIENT_ID, currency: 'EUR', intent: 'capture' }}
                >
                  <PayPalButtons
                    style={{ layout: 'vertical', shape: 'pill', label: 'paypal' }}
                    forceReRender={[amount]}
                    createOrder={paypalActions.createOrder}
                    onApprove={paypalActions.onApprove}
                    onError={paypalActions.onError}
                  />
                </PayPalScriptProvider>
              ) : (
                <p className="text-sm text-gray-500 italic">Configura el importe para ver las opciones de PayPal.</p>
              )}
            </div>
          )}

          {tab === 'transfer' && (
            <div data-testid="transfer-form">
              <div className="bg-[#FDFBF5] border border-[#D4A84B]/30 rounded-xl p-4 mb-5 text-sm text-gray-700 leading-relaxed">
                <p className="font-medium text-[#8a7428] mb-2 flex items-center gap-1.5"><Banknote className="w-4 h-4" /> Datos para el pago</p>
                <p><strong>Bizum:</strong> 654 82 73 13</p>
                <p><strong>Transferencia:</strong> ES43 1563 2626 3332 6654 3475 · Hermi Vázquez</p>
                <p className="text-xs text-gray-500 mt-2">Indica en el concepto: <em>REINICIA + tu nombre</em></p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <label>
                  <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Método</span>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
                    data-testid="payment-method"
                  >
                    <option value="transfer">Transferencia</option>
                    <option value="bizum">Bizum</option>
                  </select>
                </label>
              </div>

              <label className="block mb-3">
                <span className="block text-xs uppercase tracking-wider text-gray-500 mb-2">Comprobante (imagen o captura)</span>
                <CloudinaryUpload
                  value={receiptUrl}
                  onChange={setReceiptUrl}
                  folder="escuela/retiros/comprobantes"
                  aspectRatio="video"
                  placeholder="Sube la captura de la transferencia/Bizum"
                />
              </label>

              <label className="block mb-5">
                <span className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Notas (opcional)</span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Ej: pagado por mi pareja, número de orden, etc."
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none resize-none"
                />
              </label>

              <button
                onClick={submitTransfer}
                disabled={processing || !receiptUrl}
                className="inline-flex items-center gap-2 bg-[#1a3a5c] hover:bg-[#13294b] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="submit-transfer-btn"
              >
                <Upload className="w-4 h-4" />
                {processing ? 'Enviando…' : 'Enviar comprobante'}
              </button>
              <p className="text-xs text-gray-500 mt-2">Hermi validará el comprobante en 24-48h y tu plaza pasará a estar reservada.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-sm text-emerald-800 mb-6 inline-flex items-center gap-2" data-testid="payments-completed">
          <Check className="w-4 h-4" />
          Pagado en su totalidad. ¡Nos vemos en El Olivar!
        </div>
      )}

      {/* Payment history */}
      {(enrollment.payments || []).length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Historial de pagos</p>
          <div className="space-y-2">
            {[...(enrollment.payments || [])].reverse().map(p => {
              const fs = formatStatus(p.status);
              return (
                <div key={p.id} className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm" data-testid={`payment-row-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-[hsl(var(--text-primary))]">{p.amount.toFixed(2)} €</p>
                    <p className="text-xs text-gray-500">{formatMethod(p.method)} · {new Date(p.created_at).toLocaleDateString('es-ES')}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#8a7428] hover:underline">Ver comprobante</a>}
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${fs.cls}`}>{fs.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </SectionShell>
  );
};

const StarlightSection = ({ slug, enrollment, onUpdate }) => {
  const [text, setText] = useState(enrollment.starlight_intention || '');
  const [saving, setSaving] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    api.get(`/retreats/${slug}/intentions-count`)
      .then(r => setCount(r.data?.count ?? 0))
      .catch(() => {});
  }, [slug, enrollment.starlight_intention]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/retreats/${slug}/me/starlight`, { text });
      toast.success('Intención guardada bajo las estrellas');
      const r = await api.get(`/retreats/${slug}/me`);
      onUpdate(r.data);
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };
  return (
    <SectionShell
      title="Bóveda celeste"
      subtitle="Una de las noches del retiro observaremos las estrellas en uno de los cielos más limpios de España (certificación Starlight)."
      testId="section-boveda"
    >
      <div className="relative bg-gradient-to-br from-[#0d1424] via-[#1a1f2e] to-[#2d3748] rounded-3xl p-7 mb-3 text-white overflow-hidden">
        {/* Stars decoration */}
        <div className="absolute inset-0 pointer-events-none opacity-40">
          {[...Array(20)].map((_, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: 2 + (i % 3),
                height: 2 + (i % 3),
                top: `${(i * 17) % 100}%`,
                left: `${(i * 23) % 100}%`,
                opacity: 0.4 + ((i * 7) % 5) / 10,
              }}
            />
          ))}
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#D4A84B] mb-3">
            <Star className="w-3.5 h-3.5 fill-current" /> Tu intención bajo las estrellas
          </div>
          <p className="text-sm text-white/70 leading-relaxed mb-4">
            Si quieres, deja aquí en silencio una palabra, una pregunta o un deseo que te gustaría llevar a esa noche de observación. Solo Hermi la verá.
          </p>
          <textarea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Algo que quieres soltar al cielo. O algo que quieres pedirle."
            className="w-full bg-white/10 border border-white/20 rounded-xl p-3 text-sm text-white placeholder-white/40 focus:border-[#D4A84B] focus:outline-none resize-none mb-3"
            data-testid="starlight-input"
            maxLength={1000}
          />
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#D4A84B] hover:bg-[#b89220] text-[#1a1f2e] px-5 py-2.5 rounded-full text-sm font-medium transition-colors disabled:opacity-60"
            data-testid="starlight-save"
          >
            <Star className="w-4 h-4" />
            {saving ? 'Guardando…' : 'Dejar bajo las estrellas'}
          </button>
        </div>
      </div>
      {count !== null && count > 0 && (
        <div className="text-center text-xs text-gray-500 italic mt-3" data-testid="intentions-counter" style={{ fontFamily: 'Georgia, serif' }}>
          <Star className="w-3 h-3 inline-block mr-1 text-[#D4A84B] fill-current" />
          {count === 1
            ? '1 alma ya ha dejado su intención bajo las estrellas'
            : `${count} almas ya han dejado su intención bajo las estrellas`}
        </div>
      )}
    </SectionShell>
  );
};

const PACKING_LIST = [
  { icon: Sun, title: 'Para la piscina', items: ['Bañador', 'Toalla de baño', 'Chanclas'] },
  { icon: Backpack, title: 'Para los paseos suaves', items: ['Calzado cómodo de paseo (no sólo chanclas)', 'Gorra para los paseos de mañana', 'Crema solar', 'Botella de agua'] },
  { icon: Sparkles, title: 'Para la sala', items: ['Cojín cómodo para sentarte en el suelo (importante: no hay esterillas ni cojines)', 'Si lo prefieres, habrá sillas disponibles', 'Abanico — bienvenido siempre'] },
  { icon: BedDouble, title: 'Para la habitación', items: ['Pijama y ropa cómoda', 'Útiles de aseo personales', 'Una libreta o cuaderno si te gusta escribir'] },
];

const MONASTERY_PHOTOS = [
  'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779898181/0000_0000_00000000_91_gq94xt.jpg',
  'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897874/OI000073_1_eou617.jpg',
  'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897837/2021_0418_04160900-01_ff6zfa.jpg',
  'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897832/_PM100381-01_wjc9yj.jpg',
];

const STARLIGHT_PHOTOS = [
  'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897831/2025_0821_22420600_g7gzq0.jpg',
  'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897832/2019_0512_05011600-01_ycl97u.jpg',
  'https://res.cloudinary.com/dpppkdhdk/image/upload/v1779897832/2021_0412_04195800-02_qlibys.jpg',
];

const MonasterioSection = () => {
  const [lightbox, setLightbox] = useState(null);
  return (
    <SectionShell
      title="El monasterio"
      subtitle="Un poco de la historia del lugar que nos acoge."
      testId="section-monasterio"
    >
      <div className="grid sm:grid-cols-2 gap-2.5 mb-7" data-testid="monastery-gallery">
        {MONASTERY_PHOTOS.map((url, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setLightbox(url)}
            className="group relative overflow-hidden rounded-2xl bg-gray-100 aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-[#D4A84B]"
            data-testid={`monastery-photo-${i}`}
          >
            <img src={url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </button>
        ))}
      </div>

      <div className="bg-gradient-to-br from-[#FDFBF5] via-white to-[#FBF6E8] rounded-3xl border border-[#D4A84B]/20 p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="h-px w-8 bg-[#D4A84B]" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8a7428]">Santa María de El Olivar</p>
          <span className="h-px flex-1 bg-[#D4A84B]/30" />
        </div>
        <div className="space-y-4 text-sm sm:text-[15px] text-gray-700 leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
          <p>
            El Monasterio de Santa María de El Olivar fue fundado por la <strong>Orden de la Merced en 1260</strong>
            como lugar de recogimiento y de difusión de su labor de redención de cautivos.
          </p>
          <p>
            Tras un inicio muy modesto, a inicios del siglo XVI se edificó el templo actual,
            en <strong>estilo gótico aragonés</strong>. Unos cien años más tarde, en 1628, se construye
            en cinco años un hermoso convento de estilo proto barroco junto al templo,
            con un <strong>Claustro doble de bóvedas de yesería único en España</strong>.
          </p>
          <p>
            Este conjunto ha sido restaurado por los mismos religiosos, destacando la cuidadosa
            conservación del Claustro, Refectorio y Sala Capitular, y la recuperación del
            <strong> Ábside mudéjar</strong> que queda así dentro de la zona de la Hospedería.
          </p>
          <p>
            El alojamiento, sencillo, se realiza en las <strong>habitaciones originales</strong> pero totalmente
            restauradas del claustro alto y del edificio de peregrinos, con baño individual y
            calefacción. Comemos en el <strong>refectorio original de 1628</strong>.
          </p>
          <p className="text-gray-600 not-italic" style={{ fontFamily: 'inherit' }}>
            <span className="text-[10px] uppercase tracking-wider text-[#8a7428] block mb-2">Qué se hace aquí hoy</span>
            Residencia de la Comunidad de religiosos de la Orden de la Merced ·
            Santuario de la Virgen del Olivar (patrona de la comarca) ·
            Monumento Histórico-Artístico (BIC) abierto al público · Hospedería monástica ·
            Centro de espiritualidad, turismo activo y observación astronómica.
            La Orden de la Merced lleva además la pastoral de <strong>ocho pueblos</strong> de la zona.
          </p>
        </div>
      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img src={lightbox} alt="" className="max-h-[92vh] max-w-[92vw] rounded-xl shadow-2xl" />
        </div>
      )}
    </SectionShell>
  );
};

const StarlightInfoSection = () => {
  const [lightbox, setLightbox] = useState(null);
  return (
    <SectionShell
      title="Starlight"
      subtitle="El cielo de esta tierra."
      testId="section-starlight"
    >
      <div className="relative bg-gradient-to-br from-[#0d1424] via-[#1a1f2e] to-[#2d3748] rounded-3xl p-7 sm:p-9 mb-6 text-white overflow-hidden" data-testid="starlight-hero">
        {/* Stars */}
        <div className="absolute inset-0 pointer-events-none opacity-50">
          {[...Array(28)].map((_, i) => (
            <span
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: 2 + (i % 3),
                height: 2 + (i % 3),
                top: `${(i * 13) % 100}%`,
                left: `${(i * 29) % 100}%`,
                opacity: 0.3 + ((i * 7) % 6) / 10,
              }}
            />
          ))}
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <Telescope className="w-5 h-5 text-[#D4A84B]" />
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4A84B]">Experiencia de profundidad estelar</p>
          </div>
          <p className="text-white/85 leading-relaxed text-base sm:text-lg" style={{ fontFamily: 'Georgia, serif' }}>
            El Monasterio ha sido <strong className="text-white">certificado por la Fundación Starlight</strong>
            por la calidad de sus cielos y el rigor de sus actividades. Una de las noches del retiro
            saldremos a observar las estrellas dentro del programa <em>"El Cielo de esta Tierra"</em>,
            uno de los firmamentos más limpios y oscuros de la península.
          </p>
          <p className="text-white/65 leading-relaxed text-sm mt-4">
            No hace falta saber nada de astronomía. Sólo dejarse mirar.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-2.5" data-testid="starlight-gallery">
        {STARLIGHT_PHOTOS.map((url, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setLightbox(url)}
            className="group relative overflow-hidden rounded-2xl bg-black aspect-[4/3] focus:outline-none focus:ring-2 focus:ring-[#D4A84B]"
            data-testid={`starlight-photo-${i}`}
          >
            <img src={url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </button>
        ))}
      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img src={lightbox} alt="" className="max-h-[92vh] max-w-[92vw] rounded-xl shadow-2xl" />
        </div>
      )}
    </SectionShell>
  );
};

const CarpoolSection = ({ slug }) => {
  const [posts, setPosts] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    role: 'driver', // driver | passenger
    from_city: '', depart_date: '', return_date: '',
    seats: 1, contact: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [myId, setMyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/retreats/${slug}/carpool`);
      setPosts(r.data?.posts || []);
      setMyId(r.data?.my_user_id || null);
    } catch { setPosts([]); }
  };
  useEffect(() => { load(); }, [slug]);

  const submit = async () => {
    if (!form.from_city.trim() || !form.contact.trim()) {
      toast.error('Indica al menos la ciudad y un contacto');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/retreats/${slug}/carpool`, form);
      toast.success('Anuncio publicado');
      setShowForm(false);
      setForm({ role: 'driver', from_city: '', depart_date: '', return_date: '', seats: 1, contact: '', notes: '' });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const removeMine = async (id) => {
    if (!window.confirm('¿Borrar este anuncio?')) return;
    try {
      await api.delete(`/retreats/${slug}/carpool/${id}`);
      toast.success('Anuncio eliminado');
      await load();
    } catch { toast.error('Error'); }
  };

  return (
    <SectionShell title="Coches compartidos" subtitle="Llegar al monasterio acompañadas. Es más bonito juntas." testId="section-coches">
      {/* Intro encouraging publishing */}
      <div className="bg-gradient-to-br from-[#FDFBF5] via-white to-[#FBF6E8] border border-[#D4A84B]/25 rounded-2xl p-5 sm:p-6 mb-6" data-testid="carpool-intro">
        <div className="flex items-center gap-2 mb-3">
          <Car className="w-5 h-5 text-[#8a7428]" />
          <p className="text-sm font-medium text-[#5e4f1a]">Te animamos a publicar tu anuncio</p>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed mb-3">
          Vengas en coche o sin él, pásate por aquí: <strong>tanto si llevas plazas como si buscas
          quién pueda acercarte</strong>. Cuantas más personas escribamos, más fácil será coordinarnos
          y ahorrar trayectos, dinero y kilómetros al planeta.
        </p>
        <p className="text-xs text-gray-500">
          Puedes <em>responder en los anuncios de otras personas</em> dejando un comentario para
          ofrecerte o preguntar lo que necesites.
        </p>
      </div>

      <div className="flex justify-between items-center mb-5">
        <div className="text-xs text-gray-500 italic">
          {posts === null ? 'Cargando…' : `${posts.length} ${posts.length === 1 ? 'anuncio' : 'anuncios'} publicados`}
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1.5 bg-[#2d3748] hover:bg-black text-white px-4 py-2 rounded-full text-xs font-medium"
          data-testid="carpool-new-btn"
        >
          <Plus className="w-3.5 h-3.5" /> {showForm ? 'Cerrar' : 'Publicar anuncio'}
        </button>
      </div>

      {showForm && (
        <div className="bg-[#FDFBF5] border border-[#D4A84B]/20 rounded-2xl p-5 mb-6 space-y-3" data-testid="carpool-form">
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: 'driver',    l: 'Ofrezco plazas en mi coche' },
              { v: 'passenger', l: 'Busco coche para llegar' },
            ].map(o => (
              <button
                key={o.v}
                type="button"
                onClick={() => setForm({ ...form, role: o.v })}
                className={`px-3 py-2.5 rounded-xl text-xs border transition-colors flex items-center justify-center gap-1.5 ${
                  form.role === o.v ? 'bg-[#D4A84B]/15 border-[#D4A84B] text-[#5e4f1a] font-medium' : 'bg-white border-gray-200 text-gray-600'
                }`}
                data-testid={`carpool-role-${o.v}`}
              >
                {o.v === 'driver' ? <Car className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
                {o.l}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Salgo desde *</span>
              <input
                type="text"
                value={form.from_city}
                onChange={(e) => setForm({ ...form, from_city: e.target.value })}
                placeholder="Ciudad o pueblo"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
                data-testid="carpool-city"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Fecha ida</span>
              <input
                type="date"
                value={form.depart_date}
                onChange={(e) => setForm({ ...form, depart_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Fecha vuelta</span>
              <input
                type="date"
                value={form.return_date}
                onChange={(e) => setForm({ ...form, return_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
              />
            </label>
            {form.role === 'driver' && (
              <label className="block">
                <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Plazas libres</span>
                <input
                  type="number"
                  min={1}
                  max={6}
                  value={form.seats}
                  onChange={(e) => setForm({ ...form, seats: Number(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
                />
              </label>
            )}
          </div>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Contacto * (teléfono o email)</span>
            <input
              type="text"
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              placeholder="Teléfono o email"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none"
              data-testid="carpool-contact"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Notas (opcional)</span>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Hora aprox. de salida, puntos de recogida por el camino, etc."
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-[#D4A84B] focus:outline-none resize-none"
            />
          </label>
          <button
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#2d3748] hover:bg-black text-white px-4 py-2 rounded-full text-xs font-medium disabled:opacity-60"
            data-testid="carpool-submit"
          >
            <Plus className="w-3.5 h-3.5" />
            {saving ? 'Publicando…' : 'Publicar anuncio'}
          </button>
        </div>
      )}

      {posts && posts.length === 0 ? (
        <Placeholder text="Aún no hay anuncios. Sé la primera persona en publicar." />
      ) : (
        <div className="space-y-4">
          {(posts || []).map(p => (
            <CarpoolPostCard
              key={p.id}
              post={p}
              myId={myId}
              onDelete={removeMine}
              onCommentAdded={load}
              slug={slug}
            />
          ))}
        </div>
      )}
    </SectionShell>
  );
};

const CarpoolPostCard = ({ post, myId, onDelete, onCommentAdded, slug }) => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const isMine = myId && post.user_id === myId;
  const submit = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/retreats/${slug}/carpool/${post.id}/comments`, { text: text.trim() });
      setText('');
      await onCommentAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al comentar');
    } finally { setSending(false); }
  };
  const fmtRelative = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) + ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className={`relative bg-white rounded-2xl border overflow-hidden transition-shadow hover:shadow-md ${
      post.role === 'driver' ? 'border-emerald-200/60' : 'border-blue-200/60'
    }`} data-testid={`carpool-post-${post.id}`}>
      {/* Left accent stripe */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${post.role === 'driver' ? 'bg-emerald-400' : 'bg-blue-400'}`} />

      <div className="p-4 sm:p-5 pl-5 sm:pl-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                post.role === 'driver' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'
              }`}>
                {post.role === 'driver'
                  ? <><Car className="w-3 h-3" /> Ofrece coche</>
                  : <><Users className="w-3 h-3" /> Busca coche</>}
              </span>
              {post.role === 'driver' && (
                <span className="text-[10px] text-gray-500 bg-gray-50 px-2 py-0.5 rounded-full">
                  {post.seats} {post.seats === 1 ? 'plaza libre' : 'plazas libres'}
                </span>
              )}
            </div>
            <p className="text-base font-medium text-[hsl(var(--text-primary))]">
              <span className="text-[#8a7428]">{post.user_name || 'Una persona del retiro'}</span>
              <span className="text-gray-500 font-normal"> · sale desde </span>
              <strong className="text-slate-800">{post.from_city}</strong>
            </p>
            <div className="text-xs text-gray-500 mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
              {post.depart_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Ida: {post.depart_date}</span>}
              {post.return_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Vuelta: {post.return_date}</span>}
            </div>
            {post.notes && (
              <p className="text-sm text-gray-600 italic mt-2.5 bg-gray-50/60 rounded-lg p-2.5" style={{ fontFamily: 'Georgia, serif' }}>
                "{post.notes}"
              </p>
            )}
            <p className="text-xs text-[#8a7428] mt-2.5 inline-flex items-center gap-1.5">
              <MessageCircle className="w-3 h-3" />
              <span>Contacto:</span>
              <strong>{post.contact}</strong>
            </p>
          </div>
          {isMine && (
            <button
              onClick={() => onDelete(post.id)}
              className="text-red-500 hover:bg-red-50 p-1.5 rounded-full"
              title="Borrar mi anuncio"
              data-testid={`carpool-delete-${post.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Comments */}
        {(post.comments || []).length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100 space-y-2.5" data-testid={`carpool-comments-${post.id}`}>
            {post.comments.map(c => (
              <div key={c.id} className="flex gap-2.5">
                <div className="w-7 h-7 rounded-full bg-[#FBF6E8] flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-3 h-3 text-[#8a7428]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-700">
                    <strong className="text-[#8a7428]">{c.user_name || 'Una persona'}</strong>
                    <span className="text-gray-400 font-normal text-[10px] ml-2">{fmtRelative(c.created_at)}</span>
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-0.5">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Comment composer */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-start gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !sending) submit(); }}
            placeholder={isMine ? 'Responde a quien comente…' : 'Escríbele a esta persona…'}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-full text-sm focus:border-[#D4A84B] focus:outline-none"
            data-testid={`carpool-comment-input-${post.id}`}
          />
          <button
            onClick={submit}
            disabled={sending || !text.trim()}
            className="inline-flex items-center gap-1 bg-[#2d3748] hover:bg-black text-white px-3 py-2 rounded-full text-xs disabled:opacity-50"
            data-testid={`carpool-comment-send-${post.id}`}
          >
            <Send className="w-3 h-3" />
            <span className="hidden sm:inline">Enviar</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const WHATSAPP_NUM = '34654827313';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUM}?text=${encodeURIComponent('Hola Hermi, te escribo por el retiro REINICIA 2026.')}`;

const CommsSection = ({ slug }) => {
  const [messages, setMessages] = useState(null);
  useEffect(() => {
    api.get(`/retreats/${slug}/broadcasts`)
      .then(r => setMessages(r.data || []))
      .catch(() => setMessages([]));
  }, [slug]);

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) +
        ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <SectionShell title="Comunicaciones" subtitle="Avisos y mensajes que envía Hermi al grupo del retiro." testId="section-comms">
      {/* Direct WhatsApp CTA */}
      <a
        href={WHATSAPP_LINK}
        target="_blank" rel="noopener noreferrer"
        className="block bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-5 mb-6 hover:border-emerald-400 transition-colors"
        data-testid="comms-whatsapp"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800">¿Una duda urgente? Escríbele a Hermi por WhatsApp</p>
            <p className="text-xs text-slate-500">+34 654 82 73 13 · respuesta directa</p>
          </div>
          <span className="text-xs text-emerald-700 font-medium hidden sm:inline">Abrir →</span>
        </div>
      </a>

      {messages === null && <Placeholder text="Cargando…" />}
      {messages && messages.length === 0 && (
        <Placeholder text="Todavía no hay mensajes. Cuando Hermi envíe un aviso al grupo aparecerá aquí." />
      )}
      {messages && messages.length > 0 && (
        <div className="space-y-3" data-testid="comms-list">
          {messages.map(m => (
            <div key={m.id} className="bg-white border border-gray-100 rounded-2xl p-5" data-testid={`broadcast-${m.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#FBF6E8] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-[#8a7428]" />
                </div>
                <div className="flex-1 min-w-0">
                  {m.title && <p className="font-heading text-base text-slate-800 mb-1">{m.title}</p>}
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">
                    {m.author_name || 'Hermi'} · {fmt(m.created_at)}
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{m.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );
};

const DocsSection = ({ retreat }) => (
  <SectionShell title="Documentos y logística" subtitle="Todo lo práctico en un solo sitio." testId="section-docs">
    {/* Cómo llegar */}
    <div className="mb-7">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-px w-6 bg-[#D4A84B]" />
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a7428]">Cómo llegar</p>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed">{retreat.location_address}</p>
      <a
        href={`https://www.google.com/maps?q=${encodeURIComponent(retreat.location_address)}`}
        target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-[#8a7428] hover:underline text-xs mt-2"
        data-testid="open-maps-link"
      >
        <MapPin className="w-3 h-3" /> Abrir en Google Maps
      </a>
    </div>

    {/* Qué llevar — packing list */}
    <div className="mb-7" data-testid="packing-list">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-px w-6 bg-[#D4A84B]" />
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a7428]">Qué llevar</p>
      </div>
      <div className="bg-gradient-to-br from-amber-50/40 via-[#FDFBF5] to-white rounded-2xl border border-[#D4A84B]/15 p-5 mb-4">
        <p className="text-sm text-gray-700 leading-relaxed italic" style={{ fontFamily: 'Georgia, serif' }}>
          En Teruel suele hacer <strong className="text-amber-700 not-italic">calor</strong>, pero dentro del monasterio
          se está <strong className="text-blue-700 not-italic">fresquito</strong>. Trae capas.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {PACKING_LIST.map((cat, idx) => {
          const Icon = cat.icon;
          return (
            <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-[#D4A84B]" />
                <p className="text-sm font-medium text-[hsl(var(--text-primary))]">{cat.title}</p>
              </div>
              <ul className="space-y-1.5">
                {cat.items.map((item, i) => (
                  <li key={i} className="flex gap-2 text-xs text-gray-600 leading-relaxed">
                    <span className="text-[#D4A84B] flex-shrink-0">·</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="mt-4 bg-[#FDFBF5] border border-[#D4A84B]/30 rounded-xl p-4 text-xs text-[#5e4f1a] leading-relaxed">
        <strong>Importante:</strong> en la sala de prácticas <strong>no hay esterillas ni cojines</strong>.
        Como mínimo trae un cojín cómodo para sentarte en el suelo. Habrá sillas para quien lo necesite.
      </div>
    </div>

    {/* Información práctica */}
    {retreat.practical_info && (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="h-px w-6 bg-[#D4A84B]" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a7428]">Información práctica</p>
        </div>
        <p className="whitespace-pre-line text-sm text-gray-700 leading-relaxed">{retreat.practical_info}</p>
      </div>
    )}
  </SectionShell>
);

const FaqSection = ({ retreat }) => (
  <SectionShell title="Preguntas frecuentes" subtitle="Las dudas que más se repiten." testId="section-faq">
    <div className="space-y-4">
      {(retreat.faqs || []).map((f, i) => (
        <div key={i} className="border-l-2 border-[#D4A84B]/40 pl-4">
          <p className="font-medium text-[hsl(var(--text-primary))] mb-1 text-sm">{f.q}</p>
          <p className="text-sm text-gray-600 leading-relaxed">{f.a}</p>
        </div>
      ))}
    </div>
  </SectionShell>
);

export default RetreatPrivate;
